// src/App.js
import React, { useEffect, useState } from "react";
import { Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import "./App.css";
import Layout from "./components/Layout";
import Orders from "./components/Orders";
import Login from "./components/Login";
import ProjectDetail from "./components/ProjectDetail";
import Tasks from "./components/Tasks";
import Clients from "./components/Clients";
import Analytics from "./components/Analytics";
import Schedule from "./components/Schedule";
import { CircularProgress, Box } from "@mui/material";
import { PermissionsProvider, usePermissions } from "./permissions/PermissionsProvider";
import { ACTIONS } from "./permissions/definition";
import { CloudSaveToaster } from "./components/CloudSaveToast";

const AppRoutes = ({ user }) => {
  const { can, defaultRoute, loading: permsLoading, loadedFor } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (permsLoading || (user && loadedFor !== user.email.toLowerCase())) return;

    if (!user) {
      if (location.pathname !== "/login") {
        navigate("/login", { replace: true });
      }
      return;
    }

    if (location.pathname === "/login") {
      navigate(defaultRoute, { replace: true });
      return;
    }

    const path = location.pathname;
    if (path === "/" && !can(ACTIONS.NAV_VIEW_PROJECTS)) {
      navigate(defaultRoute, { replace: true });
      return;
    }
    if (path.startsWith("/project") && !can(ACTIONS.PROJECT_VIEW_DETAIL)) {
      navigate(defaultRoute, { replace: true });
      return;
    }
    if (path.startsWith("/clients") && !can(ACTIONS.NAV_VIEW_CLIENTS)) {
      navigate(defaultRoute, { replace: true });
      return;
    }
    if (path.startsWith("/analytics") && !can(ACTIONS.NAV_VIEW_ANALYTICS)) {
      navigate(defaultRoute, { replace: true });
      return;
    }
    if (path.startsWith("/schedule") && !can(ACTIONS.NAV_VIEW_SCHEDULE)) {
      navigate(defaultRoute, { replace: true });
      return;
    }
    if (path.startsWith("/tasks") && !can(ACTIONS.NAV_VIEW_TASKS)) {
      navigate(defaultRoute, { replace: true });
    }
  }, [user, location.pathname, can, defaultRoute, navigate, permsLoading, loadedFor]);

  return user ? (
    <Layout user={user}>
      <Routes>
        <Route path="/" element={<Orders user={user} />} />
        <Route path="/project/:id" element={<ProjectDetail user={user} />} />
        <Route path="/tasks" element={<Tasks user={user} />} />
        <Route path="/schedule" element={<Schedule user={user} />} />
        <Route path="/clients" element={<Clients user={user} />} />
        <Route path="/analytics" element={<Analytics user={user} />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Layout>
  ) : (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
  );
};

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PermissionsProvider user={user}>
      <>
        <CloudSaveToaster />
        <AppRoutes user={user} />
      </>
    </PermissionsProvider>
  );
};

export default App;
